/**
 * Prescription mock adapter (T33). Same contract as the real client so
 * flipping `VITE_USE_MOCKS` is a no-op for callers.
 */
import { delay } from "./registry";
import type {
  PrescriptionDetail,
  PrescriptionItem,
  PrescriptionListFilter,
  PrescriptionSummary,
} from "../../types/prescription";
import type { PageResponse } from "../../types/patient";

const STORE: PrescriptionDetail[] = [
  {
    id: "rx-1001",
    patientId: "p-1",
    patientName: "Nguyễn Văn An",
    patientCode: "BN-0001",
    patientPhone: "+84901234567",
    doctorId: "d-1",
    doctorName: "BS. Trần Thị Bình",
    status: "SIGNED",
    encounterId: "enc-1",
    itemCount: 2,
    validityStart: new Date().toISOString().slice(0, 10),
    validityEnd: null,
    createdAt: new Date().toISOString(),
    allergyConflicts: [],
    items: [
      {
        id: "it-1",
        medicineId: "m-1",
        medicineName: "Paracetamol",
        medicineStrength: "500mg",
        dosage: "Sáng 1 - Tối 1 viên",
        frequency: "2 lần/ngày",
        route: "Uống",
        durationDays: 5,
        quantity: 10,
        unit: "viên",
        instructions: "Sau ăn",
        itemOrder: 0,
      },
      {
        id: "it-2",
        medicineId: "m-2",
        medicineName: "Amoxicillin",
        medicineStrength: "500mg",
        dosage: "Sáng 1 - Trưa 1 - Tối 1 viên",
        frequency: "3 lần/ngày",
        route: "Uống",
        durationDays: 7,
        quantity: 21,
        unit: "viên",
        instructions: "Trước ăn",
        itemOrder: 1,
      },
    ],
  },
];

function summaryOf(p: PrescriptionDetail): PrescriptionSummary {
  const { items: _items, ...s } = p;
  void _items;
  return s;
}

export const mockPrescriptions = {
  async list(
    filter: PrescriptionListFilter | undefined,
    pageable: { page?: number; size?: number }
  ): Promise<PageResponse<PrescriptionSummary>> {
    let all = STORE.slice();
    if (filter?.status) all = all.filter((p) => p.status === filter.status);
    if (filter?.q) {
      const q = filter.q.toLowerCase();
      all = all.filter((p) => p.patientName.toLowerCase().includes(q));
    }
    const size = pageable.size ?? 20;
    const page = pageable.page ?? 0;
    return delay({
      content: all.slice(page * size, page * size + size).map(summaryOf),
      totalElements: all.length,
      totalPages: Math.max(1, Math.ceil(all.length / size)),
      number: page,
      size,
    });
  },

  async get(id: string): Promise<PrescriptionDetail> {
    const p = STORE.find((x) => x.id === id);
    if (!p) throw new Error("Không tìm thấy đơn thuốc");
    return delay({ ...p, items: [...p.items] });
  },

  async createFromEncounter(encounterId: string): Promise<PrescriptionDetail> {
    const existing = STORE.find((p) => p.encounterId === encounterId);
    if (existing) return delay({ ...existing });
    const fresh: PrescriptionDetail = {
      ...STORE[0],
      id: `rx-${Math.random().toString(36).slice(2, 8)}`,
      status: "DRAFT",
      encounterId,
      createdAt: new Date().toISOString(),
    };
    STORE.push(fresh);
    return delay({ ...fresh });
  },

  async updateItems(
    _id: string,
    _items: Omit<
      PrescriptionItem,
      "id" | "itemOrder" | "medicineName" | "medicineStrength"
    >[]
  ): Promise<void> {
    void _id;
    void _items;
    return delay(undefined);
  },

  async finalize(id: string): Promise<void> {
    const p = STORE.find((x) => x.id === id);
    if (p) p.status = "SIGNED";
    return delay(undefined);
  },

  async cancel(id: string, reason: string): Promise<void> {
    void reason;
    const p = STORE.find((x) => x.id === id);
    if (p) p.status = "CANCELLED";
    return delay(undefined);
  },
};
